import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { getTenant, getTenants } from "../api";

export const useTenantStore = create(
  immer((set, get) => ({
    tenants: {},
    allTenants: { totalCount: 0, data: [] }, // For listing
    tenantOptions: [], // Precomputed options for selects (stable reference)
    loading: {}, // Track loading state by tenant ID
    errors: {}, // Track error state by tenant ID
    isAllTenantsLoading: false,
    allTenantsError: null,

    // Fetch all tenants for dropdowns
    fetchAllTenants: async (forceRefresh = false) => {
      const existingData = get().allTenants;
      if (existingData.data.length > 0 && !forceRefresh) {
        return existingData;
      }

      set((state) => {
        state.isAllTenantsLoading = true;
        state.allTenantsError = null;
      });

      try {
        // Fetch a large page size to get all tenants for the dropdown
        const data = await getTenants(1, 1000);
        set((state) => {
          state.allTenants = data;
          state.tenantOptions = (data.data || []).map((t) => ({
            value: t.id,
            label: t.name,
          }));
          state.isAllTenantsLoading = false;
          // Also cache individual tenants
          if (data.data) {
            data.data.forEach((tenant) => {
              state.tenants[tenant.id] = tenant;
            });
          }
        });
        return data;
      } catch (error) {
        set((state) => {
          state.allTenantsError = error.message || "Failed to load tenants";
          state.isAllTenantsLoading = false;
        });
        throw error;
      }
    },

    // Paginated fetch for listing pages
    fetchTenants: async (page, pageSize) => {
      const data = await getTenants(page, pageSize);
      set((state) => {
        state.allTenants = data;
        state.tenantOptions = (data.data || []).map((t) => ({
          value: t.id,
          label: t.name,
        }));
      });
      return data;
    },

    // Smart fetch - gets from cache or API
    fetchTenant: async (id, forceRefresh = false) => {
      // If we already have a complete tenant and don't need to refresh
      const existingTenant = get().tenants[id];
      if (existingTenant && !forceRefresh) {
        return existingTenant;
      }

      // Mark as loading
      set((state) => {
        state.loading[id] = true;
        state.errors[id] = null;
      });

      try {
        const data = await getTenant(id);
        set((state) => {
          state.tenants[id] = data;
          state.loading[id] = false;
          // If this tenant wasn't already in allTenants, optionally add & rebuild options
          if (!state.allTenants.data.find((t) => t.id === id)) {
            state.allTenants.data.push(data);
            state.tenantOptions = state.allTenants.data.map((t) => ({
              value: t.id,
              label: t.name,
            }));
            state.allTenants.totalCount = state.allTenants.data.length;
          }
        });
        return data;
      } catch (error) {
        set((state) => {
          state.errors[id] = error.message || "Failed to load tenant";
          state.loading[id] = false;
        });
        throw error;
      }
    },

    // Update a tenant in the store
    updateTenant: (id, data) =>
      set((state) => {
        if (!state.tenants[id]) state.tenants[id] = {};
        Object.assign(state.tenants[id], data);
      }),

    // Get loading state for a specific tenant
    isTenantLoading: (id) => get().loading[id] || false,

    // Get error state for a specific tenant
    getTenantError: (id) => get().errors[id] || null,

    // Clear all data
    clearAll: () =>
      set((state) => {
        state.tenants = {};
        state.allTenants = { totalCount: 0, data: [] };
        state.tenantOptions = [];
        state.loading = {};
        state.errors = {};
        state.isAllTenantsLoading = false;
        state.allTenantsError = null;
      }),
  }))
);
